import React, { useEffect, useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import AuthLayout from "./AuthLayout";
import { MailIcon, LockIcon, EyeIcon, EyeOffIcon } from "./AuthIcons";
import { useAuthContext } from "../../contexts/AuthContext";
import { getAuthErrorMessage } from "../../services/authService";
import { validateEmail } from "../../utils/validators";
import { isAdminRole } from "../../utils/roles";
import Toast from "../../components/Common/Toast";

const SignInPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuthContext();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState<{ type: "success" | "error"; message: string } | null>(null);
  const [fieldErrors, setFieldErrors] = useState<{ email?: string; password?: string }>({});

  // Where to go after a successful login (defaults to the dashboard).
  const from = (location.state as any)?.from?.pathname || "/";

  // Show the one-time success notice forwarded from signup / password-reset.
  useEffect(() => {
    const notice = (location.state as any)?.notice as string | undefined;
    if (notice) {
      setToast({ type: "success", message: notice });
      // Clear it so it doesn't reappear on refresh/back.
      navigate(location.pathname, { replace: true, state: {} });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const errs: { email?: string; password?: string } = {};
    if (!email.trim()) errs.email = "Email is required";
    else if (!validateEmail(email)) errs.email = "Enter a valid email address";
    if (!password) errs.password = "Password is required";

    setFieldErrors(errs);
    if (Object.keys(errs).length > 0) return;

    try {
      setLoading(true);
      const loggedIn = await login(email.trim(), password);
      setToast({ type: "success", message: `Welcome back, ${loggedIn.fname || "there"}!` });
      // Admins don't have a user-app home yet — send them straight to /admin.
      const destination = isAdminRole(loggedIn.role) ? "/admin" : from;
      // Brief pause so the success toast is visible before redirecting.
      setTimeout(() => navigate(destination, { replace: true }), 700);
    } catch (err: any) {
      setToast({ type: "error", message: getAuthErrorMessage(err, "Invalid credentials") });
      setLoading(false);
    }
  };

  return (
    <AuthLayout>
      <p className="auth-eyebrow">Welcome back</p>
      <h2>Sign in to your account</h2>
      <p className="auth-subtitle">Enter your details to continue to your dashboard.</p>

      <form onSubmit={handleSubmit} noValidate>
        <div className={`auth-field ${fieldErrors.email ? "has-error" : ""}`}>
          <label htmlFor="email">Email address</label>
          <div className="auth-input-wrap">
            <span className="auth-icon">
              <MailIcon />
            </span>
            <input
              id="email"
              name="email"
              type="email"
              placeholder="you@company.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
            />
          </div>
          {fieldErrors.email && <span className="auth-field-error">{fieldErrors.email}</span>}
        </div>

        <div className={`auth-field ${fieldErrors.password ? "has-error" : ""}`}>
          <label htmlFor="password">Password</label>
          <div className="auth-input-wrap">
            <span className="auth-icon">
              <LockIcon />
            </span>
            <input
              id="password"
              name="password"
              className="has-eye"
              type={showPassword ? "text" : "password"}
              placeholder="••••••••••"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
            />
            <button
              type="button"
              className="auth-eye"
              onClick={() => setShowPassword((s) => !s)}
              aria-label={showPassword ? "Hide password" : "Show password"}
            >
              {showPassword ? <EyeOffIcon /> : <EyeIcon />}
            </button>
          </div>
          {fieldErrors.password && (
            <span className="auth-field-error">{fieldErrors.password}</span>
          )}
        </div>

        <div className="auth-options">
          <label className="auth-remember">
            <input
              type="checkbox"
              checked={remember}
              onChange={(e) => setRemember(e.target.checked)}
            />
            Remember me
          </label>
          <Link to="/forgot-password" className="auth-link">
            Forgot password?
          </Link>
        </div>

        <button type="submit" className="auth-submit" disabled={loading}>
          {loading && <span className="auth-spinner" />}
          {loading ? "Signing in..." : "Sign in"}
        </button>
      </form>

      <p className="auth-foot">
        Need an account? <Link to="/signup">Create one</Link>
      </p>

      {toast && (
        <Toast type={toast.type} message={toast.message} onClose={() => setToast(null)} />
      )}
    </AuthLayout>
  );
};

export default SignInPage;
